/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect, useState } from "react";
import { IRouteInfo, ADMINISTRATIONLIST, DIALOGOPENFOR } from "../../globals/globals";
import { Outlet, useLocation } from "react-router-dom";
import AdminNavigationList from "../../components/Layout/AdminNavigationList";
import { useSelector } from "react-redux";
import { basicDialogSelector } from "../../store/helpers/helperSelectors";
import AddSiteModal from "./OfflineDevices/AddsiteSteps/AddSiteModal";
import BulkOfflineStatusModal from "./OfflineDevices/BulkOfflineStatusModal";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarInset,
  SidebarTrigger,
  SidebarRail,
} from "../../components/ui/sidebar";
import { Separator } from "../../components/ui/separator";

const Administrations: React.FC = () => {
  const adminNavList: IRouteInfo[] = Object.values(ADMINISTRATIONLIST).filter(
    (route) => route.Path !== ADMINISTRATIONLIST.TRISYNC.Path
  );

  const location = useLocation();
  const basicDialodOpenFor = useSelector(basicDialogSelector);

  const getActiveIndex = () => {
    const lastSegment = location.pathname.split("/").pop() || "";
    const match = adminNavList.find((item) => item.Path === lastSegment);
    return match?.Key ?? adminNavList[0]?.Key ?? null;
  };

  const [selectedIndex, setSelectedIndex] = useState<number | null>(getActiveIndex);

  useEffect(() => {
    setSelectedIndex(getActiveIndex());
  }, [location.pathname]);

  useEffect(() => {
    const previousRoute = localStorage.getItem("currentRoute");
    if (previousRoute) {
      localStorage.setItem("previousRoute", previousRoute);
    }
    localStorage.setItem("currentRoute", location.pathname);
  }, [location]);

  return (
    <>
      {basicDialodOpenFor === DIALOGOPENFOR.ADDSITEPOPUP && <AddSiteModal />}
      {basicDialodOpenFor === DIALOGOPENFOR.BULKSTATUSPOP && <BulkOfflineStatusModal />}

      {/* ─── Page Layout ─── */}
      <SidebarProvider
        defaultOpen={true}
        data-sidebar-page-bg
        className="h-[calc(100vh-56px)] bg-sidebar overflow-hidden"
      >
        {/* ─── Left Sidebar ─── */}
        <Sidebar
          variant="inset"
          collapsible="offcanvas"
          className="border-0 pt-2"
        >
          <SidebarHeader className="border-b border-sidebar-border h-16 justify-center">
            <div className="px-1 flex items-center gap-2">
              <SettingsIcon
                className="text-sidebar-foreground"
                sx={{ fontSize: 18 }}
              />
              <span className="text-sm font-bold text-sidebar-foreground whitespace-nowrap">
                Administration
              </span>
            </div>
          </SidebarHeader>
          <SidebarContent className="pl-3">
            <AdminNavigationList navMenuItems={adminNavList} onSelectIndex={(index) => setSelectedIndex(index)} />
          </SidebarContent>
          <SidebarRail />
        </Sidebar>

        {/* ─── Main Content (Inset Card) ─── */}
        <SidebarInset className="bg-sidebar overflow-hidden p-2">
          <div className="bg-card h-full rounded-[10px] flex flex-col overflow-hidden">
            {/* ─── Header ─── */}
            <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border px-4">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <SidebarTrigger style={{ backgroundColor: "var(--background)", color: "var(--foreground)" }} className="-ml-1" />
                <Separator
                  orientation="vertical"
                  className="mr-2 h-4 shrink-0 bg-border"
                />
                <span className="text-sm text-foreground">
                  {adminNavList.find((item) => item.Key === selectedIndex)?.Text ?? "Access Control"}
                </span>
              </div>
            </header>

            {/* ─── Route Content ─── */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-1">
              <Outlet />
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </>
  );
};

export default Administrations;